import React, {useEffect, useState} from "react";
import styled from "styled-components";
import axios from "axios";
import BodyText from "../BodyText";
import FairInfoBar from "../FairInfoBar";
import { useNavigate } from "react-router-dom";
import { RedButton } from "../Button";
import Heading from "../Heading";
import FinFormAvailable from "../../images/FinFormAvailable.png";
import FinFormNotAvailable from "../../images/FinFormNotAvailable.png";
import { handleGenericErrors, handleGenericSuccess } from "../../utils/ErrorUtility";
import { trackFinFormGetStartedButton, trackFinFormPageChange } from "../../DumbleData";

interface Props {
	language: string;
}

export const FinFormIntroPage = ( {language} : Props ) => {
    const [fairInfo, setFairInfo] = useState<any>();
    const [isFinancialFormAvailable, setIsFinancialFormAvailable] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const navigate = useNavigate();
    const showNextStep = () => {
        trackFinFormGetStartedButton();
        navigate("/financial-form/sales");
    }

    useEffect(() => {
        const getFinancialFormStatus = async () => {
            try {
                await axios.get(
                    `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/status`,
                        {
                            withCredentials: true,
                        }
                )
                .then((response) => {
                    if (response.status === 200) {
                        if (response.data.latestStep === "SUBMITTED" || response.data.latestStep === "CLOSED") {
                            navigate("/financial-form/confirmation");
                        }

                        const fairInfo = response.data.fair;
                        fairInfo.start = response.data.fair.startDate;
                        fairInfo.end = response.data.fair.endDate;
                        setFairInfo(fairInfo);
                        setIsFinancialFormAvailable(response.data.isAvailable);
                    }
                    else {
                        handleGenericSuccess(response);
                    }
                })
                .catch((err) => {
                    handleGenericErrors(err);
                });
            } catch (err: any) {
                handleGenericErrors(err);
            } finally {
                setIsLoading(false);
                trackFinFormPageChange("Financial Form Intro Page");
            }
        };
        getFinancialFormStatus();
    }, [navigate]);

	return (
        <>
            {!isLoading && (<>
                <FairInfoBar
                    language={language}
                    enableSwitch={fairInfo.enableSwitch}
                    fairInfoData={fairInfo}
                    organizationData={fairInfo}
                    pageType={"financial-form"}
                />
                {isFinancialFormAvailable && (
                    <StyledDiv>
                        <StyledHeading>{language === "en" ? "Financial Form" : "Formulaire financier"}</StyledHeading>
                        <StyledIntroPage>
                            <StyledColumn>
                                <StyledImg src={FinFormAvailable} alt="" />
                            </StyledColumn>
                            <StyledColumn>
                                <StyledSubheading>{language === "en" ? "Let's Go!" : "Allons-y!"}</StyledSubheading>
                                <StyledBodyText>{language === "en" ? "Your Book Fair Financial Form is ready to review and submit." : "Le formulaire financier de votre Festival du Livre est prêt à être révisé et soumis."}</StyledBodyText>
                                <StyledRedButton handleClick={showNextStep} >{language === "en" ? "GET STARTED" : "COMMENÇONS"}</StyledRedButton>
                            </StyledColumn>
                        </StyledIntroPage>
                    </StyledDiv>)
                }
                {!isFinancialFormAvailable && (
                    <StyledDiv>
                        <StyledHeading>{language === "en" ? "Financial Form" : "Formulaire financier"}</StyledHeading>
                        <StyledIntroPage>
                            <StyledColumn>
                                <StyledImg src={FinFormNotAvailable} alt="" />
                            </StyledColumn>
                            <StyledColumn>
                                <StyledSubheading>{language === "en" ? "Almost ready!" : "C’est presque prêt!"}</StyledSubheading>
                                <StyledBodyText>
                                    {language === "en" ? 
                                    "We're still preparing your Book Fair Financial Form, which will be accessible on your Fair Start Date. Please come back soon to review and submit your information." : 
                                    "Votre formulaire financier du Festival du Livre est en cours de préparation. Il sera accessible à partir de la date de début du Festival. Nous vous invitons à revenir bientôt pour vérifier et soumettre vos informations."}
                                </StyledBodyText>
                            </StyledColumn>
                        </StyledIntroPage>
                    </StyledDiv>)
                }
            </>)}
        </>
	);
};

export default FinFormIntroPage;

const StyledDiv = styled.div`
    max-width: 1240px;
    margin: 0 auto;
`;
const StyledIntroPage = styled.div`
    display: flex;
    justify-content: center;
    margin-left: 40px;
        margin-right: 40px;
    align-items: center;
    gap: 198px;
    margin-bottom: 64px;
    @media (max-width: 959px) {
        gap: 84px;
    }
    @media (max-width: 519px) {
        flex-direction: column;
        gap: 68px;
    }
`;
const StyledColumn = styled.div`
    flex-basis: 50%;
`;
const StyledHeading = styled(Heading)`
    margin-bottom: 56px;
    margin-top: 51px;
    margin-left: 40px;
    margin-right: 40px;
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
    text-align: left;
    @media (max-width: 519px) {
        text-align: center;
    }
`;
const StyledImg = styled.img`
    float: right;
    @media (max-width: 959px) {
        float: left;
    }
`;
const StyledSubheading = styled(Heading)`
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
    text-align: left;
    @media (max-width: 519px) {
        text-align: center;
    }
`;
const StyledBodyText = styled(BodyText)`
    font-family: museo-sans;
    font-size: 16px;
    font-weight: 500;
    line-height: 19px;
    margin-top: 16px;
    margin-bottom: 24px;
    text-align: left;
    @media (max-width: 519px) {
        margin-left: 20px;
        margin-right: 20px;
        text-align: center;
    }
`;
const StyledRedButton = styled(RedButton)`
    @media (max-width: 519px) {
        margin: 0 auto;
        display: block;
    }
`